/**
 * Toast Utilities
 * Utility functions for handling API errors and showing appropriate toasts
 */

/**
 * Handle API error and show appropriate toast
 * @param {Error} error - The error object
 * @param {Object} toast - Toast instance from useToast hook
 * @param {Object} options - Additional options
 */
export const handleApiError = (error, toast, options = {}) => {
  const {
    showToast = true,
    defaultMessage = "An error occurred",
    title = "Error",
    ...rest
  } = options;

  if (!showToast || !toast) return;

  let errorMessage = defaultMessage;
  let errorTitle = title;

  // Handle different error types
  if (error.data && error.data.error) {
    // Backend error format: { error: true, result: [...] }
    errorMessage = Array.isArray(error.data.result)
      ? error.data.result.join(", ")
      : error.data.result || error.message;
  } else if (error.message && defaultMessage === "An error occurred") {
    // Only use error.message if no custom defaultMessage was provided
    errorMessage = error.message;
  }

  // Determine error title based on status code (only if no custom title provided)
  if (error.status && title === "Error") {
    switch (error.status) {
      case 401:
        errorTitle = "Authentication Error";
        break;
      case 403:
        errorTitle = "Access Denied";
        break;
      case 404:
        errorTitle = "Not Found";
        break;
      case 422:
        errorTitle = "Validation Error";
        break;
      case 500:
        errorTitle = "Server Error";
        break;
      default:
        errorTitle = "Error";
    }
  }

  toast.error(errorMessage, {
    title: errorTitle,
    duration: 5000,
    ...rest,
  });
};

/**
 * Handle API success and show success toast
 * @param {string} message - Success message
 * @param {Object} toast - Toast instance from useToast hook
 * @param {Object} options - Additional options
 */
export const handleApiSuccess = (message, toast, options = {}) => {
  if (!toast) return;

  toast.success(message, {
    duration: 3000,
    ...options,
  });
};

/**
 * Handle API warning and show warning toast
 * @param {string} message - Warning message
 * @param {Object} toast - Toast instance from useToast hook
 * @param {Object} options - Additional options
 */
export const handleApiWarning = (message, toast, options = {}) => {
  if (!toast) return;

  toast.warning(message, {
    duration: 4000,
    ...options,
  });
};

/**
 * Handle API info and show info toast
 * @param {string} message - Info message
 * @param {Object} toast - Toast instance from useToast hook
 * @param {Object} options - Additional options
 */
export const handleApiInfo = (message, toast, options = {}) => {
  if (!toast) return;

  toast.info(message, {
    duration: 3000,
    ...options,
  });
};

export default {
  handleApiError,
  handleApiSuccess,
  handleApiWarning,
  handleApiInfo,
};
